// 07 · Onboarding — 3 pasos, solo la primera vez tras crear cuenta.
// "Saltar" siempre visible → Inicio y marca visto (localStorage +
// users/{uid}.onboardingSeen). Ilustraciones = componentes reales.
//
// ESTADOS (decisión 6 del bloque, mapeo motor ⇄ handoff):
//   handoff RADAR "zona de interés" + handoff VIGILAR → motor VIGILAR
//   4º estado del handoff → motor TEMPRANO (muestra insuficiente)
//   RADAR queda reservado para las alertas EG1 emitidas con W/L.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';
import { firestore } from '../../../firebase';
import { useAuth } from '../../../auth/AuthContext';
import usePushPermission from '../../../hooks/usePushPermission';
import { I } from '../ui';

export const OB_PENDING_KEY = 'ee-app-onboarding-pending';
export function markOnboardingSeen(uid) {
  try { localStorage.removeItem(OB_PENDING_KEY); localStorage.setItem('ee-app-onboarding-seen', '1'); } catch (_e) { /* noop */ }
  if (uid) updateDoc(doc(firestore, 'users', uid), { onboardingSeen: true }).catch(() => {});
}

const Chevron = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6E6E73" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>;

function Seg({ n }) {
  return <div className="v3seg">{[1, 2, 3].map((i) => <i key={i} className={i <= n ? 'on' : ''} />)}</div>;
}

export default function Onboarding() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const push = usePushPermission();
  const [step, setStep] = useState(1);

  const fin = () => { markOnboardingSeen(user?.uid); navigate('/app', { replace: true }); };
  const Skip = () => <button type="button" className="skip" onClick={fin}>Saltar</button>;
  const Cta = ({ children, onClick }) => (
    <button type="button" className="cta" onClick={onClick}>{children}<I.arrow /></button>
  );

  if (step === 1) return (
    <div className="v3"><div className="ob">
      <div className="ob-top"><Seg n={1} /><Skip /></div>
      <div className="ob-art"><div className="stack">
        <div className="sig"><div className="sig-t"><span className="lg">Euroliga</span><span className="v3edge">Edge −12.1</span></div><p className="match">Real Madrid<span className="vs">vs</span>Barcelona</p></div>
        <div className="sig"><div className="sig-t"><span className="lg">Bundesliga HBL</span><span className="v3edge">Edge +8.4</span></div><p className="match">THW Kiel<span className="vs">vs</span>SC Magdeburg</p></div>
        <div className="sig"><div className="sig-t"><span className="lg">NBA</span><span className="sport">🏀 Básquet</span><span className="v3edge">Edge −15.7</span></div><p className="match">Los Angeles Lakers<span className="vs">vs</span>Boston Celtics</p>
          <div className="sig-meta"><div className="c"><p className="l">Pick</p><p className="v g">UNDER 187.5</p></div><div className="c"><p className="l">Ventana</p><p className="v">Q2</p></div><div className="arw"><Chevron /></div></div></div>
      </div></div>
      <h1>Señales con <em>ventaja real.</em></h1>
      <p className="d">El motor compara la línea del mercado con su propia proyección en cada partido de básquet y handball. Solo emite cuando la diferencia es clara.</p>
      <div className="ob-foot"><Cta onClick={() => setStep(2)}>Continuar</Cta></div>
    </div></div>
  );

  if (step === 2) return (
    <div className="v3"><div className="ob">
      <div className="ob-top"><Seg n={2} /><Skip /></div>
      <div className="ob-art">
        <div className="ring"><div className="core"><i /></div></div>
        <div className="state-legend"><span className="st on"><i />Entrar</span><span className="st w"><i />Vigilar</span><span className="st r"><i />Temprano</span><span className="st e"><i />Esperar</span></div>
      </div>
      <h1>Cuatro estados. <em>Una lectura.</em></h1>
      <div className="st-list">
        <div className="it"><span className="st on"><i />Entrar</span><p>El motor confirmó la señal. Es el momento de apostar.</p></div>
        <div className="it"><span className="st w"><i />Vigilar</span><p>El partido entró en zona de interés. Ábrelo y espera la posible señal ENTRAR.</p></div>
        <div className="it"><span className="st r"><i />Temprano</span><p>Muestra insuficiente todavía. El motor espera más minutos de juego.</p></div>
        <div className="it"><span className="st e"><i />Esperar</span><p>Todavía no hay edge suficiente.</p></div>
      </div>
      <div className="ob-foot"><Cta onClick={() => setStep(3)}>Continuar</Cta></div>
    </div></div>
  );

  return (
    <div className="v3"><div className="ob">
      <div className="ob-top"><Seg n={3} /><Skip /></div>
      <div className="ob-art"><div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center', width: '100%', padding: '0 26px' }}>
        <div className="ob-notif" style={{ transform: 'scale(.92)', opacity: .6 }}><div className="ic"><I.send /></div><div style={{ flex: 1, minWidth: 0 }}><p className="k">Telegram<span>ayer</span></p><p className="tt">Señal cerrada · WIN</p><p className="dd">Milwaukee Bucks vs Phoenix Suns</p></div></div>
        <div className="ob-notif"><div className="ic"><I.bell stroke="#FF4D0D" width={18} height={18} strokeWidth="2.2" /></div><div style={{ flex: 1, minWidth: 0 }}><p className="k">Edge Elite<span>ahora</span></p><p className="tt">ENTRAR · UNDER 187.5</p><p className="dd">Lakers vs Celtics · Q2 · Edge −15.7</p></div></div>
      </div></div>
      <h1>Entérate <em>al instante.</em></h1>
      <p className="d">Cada señal llega como notificación push y, si quieres, también a tu Telegram. Puedes ajustarlo después en Perfil.</p>
      <div className="ob-foot" style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
        <Cta onClick={async () => {
          // Dispara el prompt del sistema (FCM gateado por acceso vigente;
          // sin acceso o en iOS sin instalar, el hook no registra token).
          try { if (push.canRequest) await push.request(); else if (typeof Notification !== 'undefined') await Notification.requestPermission(); } catch (_e) { /* noop */ }
          fin();
        }}>Activar notificaciones</Cta>
        <button type="button" className="cta ghost sm" onClick={fin}>Ahora no</button>
      </div>
    </div></div>
  );
}
